if (!customElements.get("carousel-slider")) {
  customElements.define(
    "carousel-slider",
    class CarouselSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.carousel = this.querySelector('[class*="grid-carousel"]');
        this.initialize();
      }

      initialize() {
        const autoplayEnabled = this.getAttribute("data-autoplay") === "true";
        const autoPlaySpeed = parseInt(this.getAttribute("data-speed"), 10) * 1000;
        const groupCells = this.getAttribute("data-group-cells") !== "false";
        const wrapAround = this.getAttribute("data-wrap-around") === "true";

        this.carousel.style.display = "block";
        this.flickity = new Flickity(this.carousel, {
          autoPlay: autoplayEnabled ? autoPlaySpeed : false,
          cellAlign: "left",
          groupCells: groupCells,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          fade: false,
          cellSelector: ".grid-item",
          initialIndex: 0,
          pageDots: false,
          wrapAround: wrapAround,
          accessibility: false,
          watchCSS: true,
        });

        this.flickity.on("change", (index) => {
          const event = new CustomEvent("slider:slide-change", {
            detail: {
              id: this.getAttribute("id"),
              index,
            },
          });
          document.dispatchEvent(event);
        });

        this.flickity.on("ready", () => this.notifyNavigation());
        this.flickity.on("resize", () => this.notifyNavigation());
        this.flickity.on("settle", () => this.notifyNavigation());
      }

      notifyNavigation() {
        const id = this.getAttribute("id");
        if (!id) return;

        document.querySelectorAll(`carousel-navigation[for="${id}"]`).forEach((navigation) => {
          if (typeof navigation.updateVisibility === "function") {
            navigation.updateVisibility();
          }
        });
      }
    },
  );
}

if (!customElements.get("carousel-navigation")) {
  customElements.define(
    "carousel-navigation",
    class CarouselNavigation extends HTMLElement {
      constructor() {
        super();
        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
        this.onFlickitySelect = this.onFlickitySelect.bind(this);
        this.onNativeScroll = this.onNativeScroll.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.unbindFlickityEvents();
        this.unbindNativeScroller();
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
      }

      init(retry = 1, maxRetry = 3) {
        if (retry === maxRetry) {
          return;
        }

        const id = this.getAttribute("for");
        this.carouselComponent = document.querySelector(`#${id}`);
        this.wrap = this.dataset.wrap === "true";

        if (!this.carouselComponent || !this.carouselComponent.flickity) {
          setTimeout(() => {
            this.init(retry + 1);
          }, 350);
          return;
        }

        this.flickity = this.carouselComponent.flickity;
        this.buttonPrev = this.querySelector(".js-prev");
        this.buttonNext = this.querySelector(".js-next");

        this.unbindFlickityEvents();
        this.bindFlickityEvents();
        this.bindButtons();
        this.bindNativeScroller();
        this.observeResize();
        this.updateVisibility();
      }

      bindButtons() {
        if (this.buttonPrev) {
          this.buttonPrev.removeEventListener("click", this.onPrevClick);
          this.buttonPrev.addEventListener("click", this.onPrevClick);
        }

        if (this.buttonNext) {
          this.buttonNext.removeEventListener("click", this.onNextClick);
          this.buttonNext.addEventListener("click", this.onNextClick);
        }
      }

      bindFlickityEvents() {
        if (!this.flickity) return;

        this.flickity.on("ready", this.updateVisibility);
        this.flickity.on("resize", this.updateVisibility);
        this.flickity.on("settle", this.updateVisibility);
        this.flickity.on("select", this.onFlickitySelect);
      }

      unbindFlickityEvents() {
        if (!this.flickity) return;

        this.flickity.off("ready", this.updateVisibility);
        this.flickity.off("resize", this.updateVisibility);
        this.flickity.off("settle", this.updateVisibility);
        this.flickity.off("select", this.onFlickitySelect);
      }

      getNativeScroller() {
        if (!this.carouselComponent) return null;

        const candidates = [
          this.carouselComponent.querySelector(".scroller-inner"),
          this.carouselComponent.querySelector(".scroller"),
        ].filter(Boolean);

        const scrollableCandidate = candidates.find((candidate) => {
          const overflowX = window.getComputedStyle(candidate).overflowX;
          const allowsScrolling = overflowX === "auto" || overflowX === "scroll";
          return allowsScrolling && candidate.scrollWidth > candidate.clientWidth + 2;
        });

        return (
          scrollableCandidate ||
          candidates.find((candidate) => candidate.scrollWidth > candidate.clientWidth + 2) ||
          null
        );
      }

      bindNativeScroller() {
        const nextScroller = this.getNativeScroller();
        if (this.nativeScroller === nextScroller) return;

        this.unbindNativeScroller();
        this.nativeScroller = nextScroller;

        if (this.nativeScroller) {
          this.nativeScroller.addEventListener("scroll", this.onNativeScroll, { passive: true });
        }
      }

      unbindNativeScroller() {
        if (!this.nativeScroller) return;
        this.nativeScroller.removeEventListener("scroll", this.onNativeScroll);
        this.nativeScroller = null;
      }

      onNativeScroll() {
        if (this.nativeScrollFrame) return;

        this.nativeScrollFrame = requestAnimationFrame(() => {
          this.nativeScrollFrame = null;
          this.updateNativeButtons();
        });
      }

      observeResize() {
        this.resizeObserver?.disconnect();

        const observeTarget =
          this.carouselComponent?.querySelector(".scroller") ||
          this.carouselComponent?.querySelector(".scroller-inner") ||
          this.carouselComponent?.carousel;

        if (!observeTarget || typeof ResizeObserver === "undefined") {
          return;
        }

        this.resizeObserver = new ResizeObserver(() => {
          this.bindNativeScroller();
          this.updateVisibility();
        });
        this.resizeObserver.observe(observeTarget);
      }

      navigationNeeded() {
        if (!this.carouselComponent) {
          return false;
        }

        if (this.flickity?.isActive) {
          const viewportWidth = this.flickity.size.innerWidth;
          const hasOverflow = this.flickity.slideableWidth > viewportWidth + 2;
          const hasMultipleSlides = this.flickity.slides.length > 1;

          return hasOverflow && hasMultipleSlides;
        }

        const nativeScroller = this.getNativeScroller();
        return nativeScroller
          ? nativeScroller.scrollWidth > nativeScroller.clientWidth + 2
          : false;
      }

      updateVisibility() {
        this.bindNativeScroller();

        const keepVisibleOnMobile =
          window.matchMedia("(max-width: 989px)").matches &&
          this.carouselComponent?.querySelectorAll(".grid-carousel .grid-item").length > 1;

        if (keepVisibleOnMobile) {
          this.classList.remove("carousel-navigation--hidden");
          this.removeAttribute("hidden");
          this.onFlickitySelect();
          return;
        }

        const needsNavigation = this.navigationNeeded();

        this.classList.toggle("carousel-navigation--hidden", !needsNavigation);

        if (needsNavigation) {
          this.removeAttribute("hidden");
          this.onFlickitySelect();
          return;
        }

        this.setAttribute("hidden", "");
      }

      onFlickitySelect() {
        if (!this.navigationNeeded() || this.wrap) {
          return;
        }

        if (!this.flickity?.isActive) {
          this.updateNativeButtons();
          return;
        }

        this.enableButton(this.buttonPrev);
        this.enableButton(this.buttonNext);

        if (!this.flickity.slides[this.flickity.selectedIndex - 1]) {
          this.disableButton(this.buttonPrev);
        } else if (!this.flickity.slides[this.flickity.selectedIndex + 1]) {
          this.disableButton(this.buttonNext);
        }
      }

      updateNativeButtons() {
        const scroller = this.nativeScroller || this.getNativeScroller();
        if (!scroller) return;

        const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        const currentScroll = Math.max(0, Math.abs(scroller.scrollLeft));

        if (currentScroll <= 2) {
          this.disableButton(this.buttonPrev);
        } else {
          this.enableButton(this.buttonPrev);
        }

        if (currentScroll >= maxScroll - 2) {
          this.disableButton(this.buttonNext);
        } else {
          this.enableButton(this.buttonNext);
        }
      }

      nativeScrollStep(scroller) {
        const items = Array.from(
          this.carouselComponent.querySelectorAll(".grid-carousel .grid-item"),
        );

        if (items.length > 1) {
          const distance = Math.abs(items[1].offsetLeft - items[0].offsetLeft);
          if (distance > 0) return distance;
        }

        if (items[0]) {
          const width = items[0].getBoundingClientRect().width;
          if (width > 0) return width;
        }

        return Math.max(1, scroller.clientWidth * 0.85);
      }

      scrollNative(direction) {
        const scroller = this.nativeScroller || this.getNativeScroller();
        if (!scroller) return;

        scroller.scrollBy({
          left: direction * this.nativeScrollStep(scroller),
          behavior: "smooth",
        });
      }

      onPrevClick(event) {
        event.preventDefault();

        if (this.flickity?.isActive) {
          this.flickity.previous();
          return;
        }

        this.scrollNative(-1);
      }

      onNextClick(event) {
        event.preventDefault();

        if (this.flickity?.isActive) {
          this.flickity.next();
          return;
        }

        this.scrollNative(1);
      }

      enableButton(button) {
        if (!button) return;
        button.classList.remove("pill-nav-item-disabled");
        button.classList.remove("focus-none");
        button.setAttribute("aria-disabled", "false");
      }

      disableButton(button) {
        if (!button) return;
        button.classList.add("pill-nav-item-disabled");
        button.classList.add("focus-none");
        button.setAttribute("aria-disabled", "true");
      }
    },
  );
}